// Texas Heart Institute · CVOR rotation. Content only; layout lives in
// components/hospital/shared. Actions are handled by HospitalDetailScreen.
import bslmcMap from '../../assets/maps/bslmc-map.png';
import cvorMap from '../../assets/maps/thi-2nd-floor-cvor.png';
import floor6Map from '../../assets/maps/thi-6th-floor.png';
import cathLabMap from '../../assets/maps/thi-6th-floor-cath-lab.png';
import { ClipboardList, ListChecks, Handshake, Pill, FlaskConical, Syringe, ClipboardPen, HeartPlus, Siren } from 'lucide-react';

const room2 = (n) => String(n).padStart(2, '0');

export const thiCvor = {
  accent: '#9F8D70',

  name: 'THI CVOR',

  // Personal notes for this rotation live under this key on the device
  notesKey: 'notes:texas-heart:cvor',

  // In-app tools, grouped into Overview sections. `featured` tiles span the
  // full width. Actions without a handler yet show a "coming soon" toast.
  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'case-setup', title: 'Case Setup Guide', subtitle: 'Drugs, lines and setup by case type', icon: ClipboardList, action: 'case-setup', featured: true },
      { id: 'drug-finder', title: 'Drug Finder', subtitle: 'Where each drug lives and what it is for', icon: Pill, action: 'drug-finder' },
      { id: 'preop', title: 'Preop Checklist', subtitle: 'Before you roll back', icon: ListChecks, action: 'preop' },
      { id: 'eras', title: 'Enhanced Recovery', subtitle: 'ERAS protocols by procedure', icon: HeartPlus, action: 'eras' },
      { id: 'handoff', title: 'Postop Handoff', subtitle: 'Memory aid for CVRR handoff', icon: Handshake, action: 'handoff' },
    ] },
    { title: 'Logistics', tools: [
      { id: 'epic', title: 'Epic', subtitle: 'What to document and where, including out-of-OR procedures', icon: ClipboardPen, action: 'epic' },
      { id: 'labs', title: 'Sending Labs', subtitle: 'Tubes, ACTs and where they go', icon: FlaskConical, action: 'labs' },
      { id: 'blocks', title: 'Nerve Blocks', subtitle: 'Regional anesthesia at THI', icon: Syringe, action: 'nerve-blocks' },
      { id: 'airways', title: 'Airways & Codes', subtitle: 'Pager schedule, what to bring, where to go', icon: Siren, action: 'airways-codes' },
    ] },
  ],

  contacts: {
    storageKey: 'thi-cvor-favorites',
    groups: [
      { id: 'charge', title: 'Charge phones', open: true, items: [
        { label: 'CV Anesthesia Charge', phone: '8323553825' },
        { label: 'CVOR Nursing Charge', phone: '8323559250' },
        { label: 'Perfusionist Charge', phone: '2817409606', note: 'ECMO, CPB, Cell Saver and ACTs' },
        { label: 'Circulatory Support', phone: '8322928963', note: 'VAD, Impella and IABP' },
      ] },
      { id: 'techs', title: 'Anesthesia techs', open: true, items: [
        { label: 'Anesthesia Tech', phone: '8323557825', note: 'CVOR' },
        { label: 'Anesthesia Tech (alternate)', phone: '8323557826', note: 'CVOR, secondary number' },
        { label: 'Anesthesia Tech: Remote', phone: '8323557827', note: 'Cath lab, endo, IR and bedside cases' },
      ] },
      { id: 'front-desk', title: 'Front desks', open: true, items: [
        { label: 'CVOR Front Desk', phone: '8323559260' },
        { label: 'Main OR Front Desk', phone: '8323552116' },
        { label: 'Cath Lab Front Desk', phone: '8323556650' },
      ] },
      { id: 'cv-anesthesia', title: 'CV Anesthesia locations', items: [
        { label: 'CV Anesthesia Office', phone: '7137986399' },
        { label: 'CV Anesthesia Blue Room', phone: '8323559268' },
        { label: 'CV Anesthesia Blue Room (alternate)', phone: '8323559269' },
      ] },
      { id: 'pharmacy', title: 'Pharmacy', items: [
        { label: 'Pharmacy Tech CVOR', phone: '8323557768', note: 'Call for any medication ordered from and compounded by Pharmacy' },
        { label: 'Central Pharmacy', phone: '8323553106' },
      ] },
      { id: 'lab', title: 'Lab and blood bank', items: [
        { label: 'Stat Lab', phone: '8323559270' },
        { label: 'Blood Bank', phone: '8323554116' },
        { label: 'Hematology Lab', phone: '8323552347' },
        { label: 'Coags', phone: '8323552349' },
      ] },
      { id: 'remote', title: 'Remote anesthesia sites', items: [
        { label: 'Endo', phone: '8323553518' },
        { label: 'Non-Invasive Cardiology / TEE', phone: '8323554138' },
        { label: 'Neuro IR', phone: '8323558478' },
      ] },
      { id: 'hospital', title: 'Hospital services', items: [
        { label: 'BSLMC IT Support', phone: '8323558000' },
        { label: 'BSLMC Phone Operator', phone: '8323551000' },
        { label: 'Propio ONE interpreters', phone: '8326622676', note: 'Account # 91555' },
        { label: 'Language Line', phone: '8337373047', note: 'Account # BSLMC' },
        { label: 'BSLMC Pacemaker Clinic', phone: '8323554928' },
      ] },
      { id: 'cvor-rooms', title: 'CVOR rooms', items: Array.from({ length: 12 }, (_, i) => ({
        label: `CVOR ${room2(i + 1)}`,
        phones: [
          { label: 'Nursing', phone: `83235592${room2(i + 1)}` },
          { label: 'Anesthesia', phone: `83235591${room2(i + 1)}` },
        ],
      })) },
      { id: 'cath-rooms', title: 'Cath lab rooms', items: Array.from({ length: 11 }, (_, i) => ({
        label: `Cath Lab ${room2(i + 1)}`,
        phone: `83235556${room2(i + 1)}`,
      })) },
      { id: 'recovery', title: 'CV recovery and PACU', items: [
        { label: 'CVRR ICU', phone: '8323553092' },
        { label: 'CVRR Charge Nurse', phone: '8323555695' },
        { label: 'CVRR Respiratory Therapist', phone: '8328854434', note: 'iNO and inhaled epoprostenol' },
        { label: 'CV PACU', phone: '8323558498' },
      ] },
      { id: 'icus', title: 'ICUs', items: [
        { label: '6 South 1 CCU', phone: '8323556610' },
        { label: '6 South 2 CCU', phone: '8323556620' },
        { label: '7 South 1 MICU', phone: '8323556710' },
        { label: '7 South 2 MICU', phone: '8323556720' },
        { label: '7 South 3 Kelsey ICU', phone: '8323556730' },
        { label: '7 South 4 Neuro ICU', phone: '8323556740' },
        { label: '7 South 5 Neuro ICU', phone: '8323556750' },
        { label: '7 South 6 Dialysis', phone: '8323556760' },
        { label: '6 Cooley A SICU', phone: '8323559610' },
        { label: '6 Cooley B', phone: '8323559620' },
        { label: '7 Cooley A Thoracic SICU', phone: '8323559710' },
        { label: '7 Cooley B', phone: '8323559720' },
        { label: '8 Cooley A Cardiac ICU', phone: '8323559810' },
        { label: '8 Cooley B', phone: '8323559820' },
      ] },
      { id: 'floors', title: 'Floor units', items: [
        { label: '6 Tower CV Outpatient', phone: '8323553346' },
        { label: '7 Tower', phone: '8323555050' },
        { label: '9 Tower', phone: '8323554094' },
        { label: '10 Tower', phone: '8323554104' },
        { label: '11 Tower', phone: '8323554114' },
        { label: '12 Tower', phone: '8323554124' },
        { label: '14 Tower', phone: '8323554144' },
        { label: '15 Tower', phone: '8323554154' },
        { label: '16 Tower', phone: '8323554164' },
        { label: '17 Tower Rehab', phone: '8323554174' },
        { label: '18 Tower', phone: '8323554184' },
        { label: '19 Tower', phone: '8323554194' },
        { label: '20 Tower', phone: '8323554204' },
        { label: '21 Tower', phone: '8323554214' },
        { label: '22 Tower', phone: '8323554224' },
        { label: '23 Tower Terrace', phone: '8323554234' },
        { label: '24 Tower', phone: '8323554244' },
        { label: '25 Tower', phone: '8323554254' },
      ] },
    ],
  },

  // Locations. Elevator banks are vertical, so the BSLMC layout map answers
  // "which elevators" for every floor; floor plans exist for 2 and 6 only.
  // `medmaps` is the department name in the hospital's MedMaps site (underscores
  // for spaces); it opens turn-by-turn directions from the Main Entrance.
  locations: {
    maps: [
      { id: 'layout', title: 'BSLMC layout', caption: 'Elevator banks, all floors', src: bslmcMap },
      { id: 'floor2', title: '2nd floor', caption: 'CVOR, CV preop, CVRR', src: cvorMap },
      { id: 'floor6', title: '6th floor', caption: 'Cath lab and 6 Cooley', src: floor6Map },
      { id: 'cathlab', title: 'Cath Lab', caption: '6th floor detail', src: cathLabMap },
    ],
    // Where each elevator bank sits on the layout map (percent of width / height)
    elevators: {
      purple: { label: 'Purple', color: '#7c3aed', x: 55.5, y: 65.1 },
      green: { label: 'Green', color: '#22c55e', x: 62.3, y: 56.7 },
      yellow: { label: 'Yellow', color: '#eab308', x: 41.9, y: 49.1 },
      orange: { label: 'Orange', color: '#f97316', x: 37.1, y: 62.8 },
      red: { label: 'Red', color: '#ef4444', x: 38.1, y: 35.2 },
    },
    groups: [
      { id: 'procedure', title: 'Procedure areas', items: [
        { name: 'CVOR', floor: '2nd floor', elevator: 'green', floorMap: 'floor2', medmaps: { to: 14162, from: 'entrance' } },
        { name: 'Cath Lab', floor: '6th floor', elevator: 'purple', floorMap: 'cathlab', code: '1234*', codeLabel: 'Cath Lab 5 door', medmaps: { to: 14187 } },
        { name: 'Non-Invasive Cardiology (TEE lab)', floor: '3rd floor', elevator: 'purple' },
        { name: 'Endo', floor: '4th floor', elevator: 'purple', medmaps: { to: 14170 } },
      ] },
      { id: 'anesthesia', title: 'Anesthesia spaces', items: [
        { name: 'CV Anesthesia Workroom', floor: '2nd floor', where: 'In the CVORs, near the Blue Room', elevator: 'green', floorMap: 'floor2', code: '5529*' },
        { name: 'CV Anesthesia Cath Lab Office', floor: '6th floor', where: 'O621, near Cath Lab 7', elevator: 'purple', floorMap: 'cathlab', code: '6720*' },
        { name: 'Resident Call Room', floor: '8th floor', where: 'Room Y835', elevator: 'yellow', code: '8872', codeNote: 'Do not share' },
        { name: 'Attending / Fellow Call Room', floor: '5th floor', where: 'Room O558', elevator: 'orange', code: '0558*' },
      ] },
      { id: 'icus', title: 'ICUs', items: [
        { name: 'CV Recovery (CVRR)', floor: '2nd floor', elevator: 'green', floorMap: 'floor2' },
        { name: '8 Cooley A and B', floor: '8th floor', elevator: 'green', note: '8A · Cardiac Surgical ICU' },
        { name: '7 Cooley A and B', floor: '7th floor', elevator: 'green', note: '7A · Thoracic Surgical ICU', medmaps: { to: 14190 } },
        { name: '6 Cooley A and B', floor: '6th floor', elevator: 'green', floorMap: 'floor6', note: '6A · Surgical ICU', medmaps: { to: 14180 } },
        { name: '7 South 1–5', floor: '7th floor', elevator: 'purple', note: '7S1–2 MICU · 7S3 Kelsey Seybold · 7S4–5 Neuro ICU', medmaps: { to: 14197 } },
      ] },
      { id: 'food', title: 'Food and lounges', items: [
        { name: 'Bertner Cafeteria', floor: 'B1', elevator: 'purple', medmaps: { to: 14146 } },
        { name: "Physicians' Lounge", floor: '2nd floor', where: 'By the Main OR front desk', elevator: 'purple', note: 'Residents welcome · Free breakfast and lunch' },
      ] },
    ],
    // MedMaps directions. `to` is the destination's direction id; `from` names an origin below (default: cvor).
    medmaps: {
      base: 'https://v2.interactive.medmaps.com/site/baylor_medctr',
      building: 861,
      origins: {
        cvor: { id: 14162, label: 'the CVOR' },
        entrance: { id: 14136, label: 'the Main Entrance' },
      },
    },
  },

  documents: {
    storageKey: 'thi-cvor-pinned-docs',
    groups: [
      { id: 'orientation', title: 'Orientation', items: [
        { title: 'Resident Handbook', url: '/thi/thi-resident-handbook.pdf' },
        { title: 'Postop Handoff Memory Aid', url: '/thi/thi-handoff-aid.pdf' },
      ] },
      { id: 'general', title: 'General protocols', items: [
        { title: 'Acute Normovolemic Hemodilution (ANH)', url: '/thi/thi-anh-protocol.pdf' },
        { title: 'AFib Prophylaxis', url: '/thi/thi-afib-ppx-protocol.pdf' },
        { title: 'Bivalirudin', action: 'bivalirudin' },
        { title: 'Central Venous Catheter Insertion', url: '/thi/thi-central-line-protocol.pdf' },
        { title: 'Insulin Infusion', action: 'insulin' },
        { title: 'ROTEM-based Transfusion', url: '/thi/thi-rotem-transfusion-protocol.pdf' },
      ] },
      { id: 'case-specific', title: 'Case-specific protocols', items: [
        { title: 'Convergent Procedure', url: '/thi/thi-convergent-protocol.pdf' },
        { title: 'Extrapleural Pneumonectomy (EPP)', url: '/thi/thi-epp-protocol.pdf' },
        { title: 'Pulmonary Thromboendarterectomy (PTE)', url: '/thi/thi-duke-pte-protocol.pdf' },
      ] },
      { id: 'equipment', title: 'Equipment and procedures', items: [
        // No files yet; hidden until a url is added
        { title: 'IV Pump Setup', url: '#' },
        { title: 'Belmont Rapid Infuser', subtitle: 'Setup, infusing, fluids and alarms', action: 'belmont' },
        { title: 'Epidural / Nerve Block Pump Programming', url: '#' },
      ] },
      { id: 'it', title: 'IT resources', items: [
        { title: 'How to Set Up Haiku', subtitle: 'Epic on your phone', action: 'haiku' },
        { title: 'How to Set Up Okta Verify', subtitle: 'CommonSpirit two-factor app', action: 'okta' },
        { title: 'Epic Ordersets & Templates', subtitle: 'By situation', action: 'epic-orders' },
      ] },
    ],
  },
};
